import React, { useEffect, useState } from 'react';
import { Alert, Button, Card, CardBody, Col, Container, Form, Input, Label, Row, Spinner } from 'reactstrap';
import api from './api';
import HistoricalFeeMapping from './HistoricalFeeMapping';

const readSession = () => {
  try {
    return JSON.parse(localStorage.getItem('authUserdetails') || 'null');
  } catch (error) {
    return null;
  }
};

function Login({ onLogin, metadata }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [working, setWorking] = useState(false);

  const submit = async (event) => {
    event.preventDefault();
    if (!email.trim() || !password) return setError('Enter your campus administrator email and password.');
    setWorking(true);
    setError('');
    try {
      const { data } = await api.post('auth/login', { admin_email: email.trim(), admin_password: password });
      localStorage.setItem('authUserdetails', JSON.stringify(data));
      onLogin(data);
    } catch (requestError) {
      setError(requestError?.response?.data?.message || 'Unable to sign in.');
    } finally {
      setWorking(false);
    }
  };

  return (
    <div className="login-shell">
      <Container>
        <Row className="justify-content-center">
          <Col xl={5} lg={6} md={8}>
            <Card className="login-card border-0">
              <CardBody className="p-4 p-md-5">
                <div className="brand-mark">AGL</div>
                <h2 className="mt-4 mb-2">Migration Review</h2>
                <p className="text-muted mb-4">Sign in with the SKOTA or VZM Administrator account. This separate workspace contains fee-migration data only.</p>
                {error && <Alert color="danger">{error}</Alert>}
                <Form onSubmit={submit}>
                  <div className="mb-3">
                    <Label for="email">Administrator email</Label>
                    <Input id="email" type="email" autoComplete="username" value={email} onChange={(event) => setEmail(event.target.value)} disabled={working} />
                  </div>
                  <div className="mb-4">
                    <Label for="password">Password</Label>
                    <Input id="password" type="password" autoComplete="current-password" value={password} onChange={(event) => setPassword(event.target.value)} disabled={working} />
                  </div>
                  <Button color="primary" size="lg" block disabled={working}>{working ? <><Spinner size="sm" className="me-2" />Signing in…</> : 'Sign in securely'}</Button>
                </Form>
                <div className="snapshot-note mt-4">
                  <strong>Review snapshot:</strong> {metadata?.snapshot_label || 'Loading…'}<br />
                  <span>{metadata?.snapshot_sha256 ? `SHA-256 ${metadata.snapshot_sha256.slice(0, 16)}…` : 'The original financial systems are not changed by this page.'}</span>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
}

export default function App() {
  const [session, setSession] = useState(readSession());
  const [metadata, setMetadata] = useState(null);

  useEffect(() => {
    api.get('health').then(({ data }) => setMetadata(data)).catch(() => setMetadata(null));
    const expire = () => setSession(null);
    window.addEventListener('agl-review-session-expired', expire);
    return () => window.removeEventListener('agl-review-session-expired', expire);
  }, []);

  const logout = () => {
    localStorage.removeItem('authUserdetails');
    setSession(null);
  };

  if (!session?.approval_token) return <Login onLogin={setSession} metadata={metadata} />;

  return (
    <>
      <header className="review-header">
        <div className="review-header-brand"><span>AGL</span> Migration Review</div>
        <div className="review-header-actions">
          <div className="text-end"><strong>{session.admin_name}</strong><small>{session.org_name}</small></div>
          <Button color="light" outline size="sm" onClick={logout}>Sign out</Button>
        </div>
      </header>
      <div className="snapshot-ribbon">
        <strong>Isolated review copy:</strong> {metadata?.snapshot_label || 'accepted 15 August 2026 snapshot'} · Production PHP/MySQL and biometric systems are not connected to this page.
      </div>
      <HistoricalFeeMapping key={`${session.security_id}:${session.org_id}`} />
    </>
  );
}
